import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiClientAmbassador } from '~/@api/client/useApiClientAmbassador'
import type {
  IAmbassadorCreateDto,
  IAmbassadorMonthlyReportXlsxExportRequest,
} from '~/@api/generated-api-types-ambassador/Api'
import { ambassadorDetailQueryKey } from './useAmbassadorDetail'

const AMBASSADOR_DATA_QUERY_KEY = ['ambassador-data']

export type MonthlyReportXlsxExportParams =
  IAmbassadorMonthlyReportXlsxExportRequest

type SingleAmbassadorMonthlyReportXlsxExportParams =
  MonthlyReportXlsxExportParams & {
    ambassadorId: string
  }

/** Senior-ambassador dashboard: overview data + create/report/deactivate/export. */
export function useAmbassadorData() {
  const api = useApiClientAmbassador()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: AMBASSADOR_DATA_QUERY_KEY,
    queryFn: async () => {
      const resp = await api.user.ambassadorControllerGetAmbassadorData({
        secure: true,
      })
      return resp.data.data
    },
  })

  const createMutation = useMutation({
    mutationFn: async (params: IAmbassadorCreateDto) =>
      api.user.ambassadorControllerPostAmbassadorCreate(params, {
        secure: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_DATA_QUERY_KEY })
    },
  })

  const reportChangeMutation = useMutation({
    mutationFn: async (text: string) =>
      api.user.ambassadorControllerPostAmbassadorReportChange(
        { text },
        { secure: true }
      ),
  })

  const setEnabledMutation = useMutation({
    mutationFn: async ({
      ambassadorId,
      isEnabled,
    }: {
      ambassadorId: string
      isEnabled: boolean
    }) =>
      api.user.ambassadorControllerAmbassadorSetIsEnabled(
        ambassadorId,
        { isEnabled },
        { secure: true }
      ),
    onSuccess: (_data, { ambassadorId }) => {
      queryClient.invalidateQueries({ queryKey: AMBASSADOR_DATA_QUERY_KEY })
      queryClient.invalidateQueries({
        queryKey: ambassadorDetailQueryKey(ambassadorId),
      })
    },
  })

  const singleAmbassadorMonthlyReportXlsxExport = useMutation({
    mutationFn: async ({
      month,
      year,
      ambassadorId,
    }: SingleAmbassadorMonthlyReportXlsxExportParams) => {
      const resp = await api.user.ambassadorControllerMonthlyReportXlsxExport(
        ambassadorId,
        { month, year },
        { secure: true }
      )
      return resp.data.data
    },
  })

  const globalMonthlyReportXlsxExport = useMutation({
    mutationFn: async ({ month, year }: MonthlyReportXlsxExportParams) => {
      const resp =
        await api.user.ambassadorControllerGlobalMonthlyReportXlsxExport(
          { month, year },
          { secure: true }
        )
      return resp.data.data
    },
  })

  return {
    query,
    createMutation,
    reportChangeMutation,
    setEnabledMutation,
    singleAmbassadorMonthlyReportXlsxExport,
    globalMonthlyReportXlsxExport,
  }
}
